/**
 * Copyright 2022 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */
export enum ACTIONS {
  StartVoiceChat = 'StartVoiceChat',
  UpdateVoiceChat = 'UpdateVoiceChat',
  StopVoiceChat = 'StopVoiceChat',
}

export interface RequestParams {
  [ACTIONS.StartVoiceChat]: {

    //你的音视频应用的唯一标志，参看获取 AppId
    AppId: string;
    //房间的 ID，是房间的唯一标志。赋值规则参看参数赋值规范。
    RoomId: string;
    //智能体任务 ID。你必须对每个智能体任务设定 TaskId，且在后续进行任务更新和结束时也须使用该 TaskId。
    //AppId + RoomId + TaskId 是任务的唯一标识，可以用来标识指定 AppId 下某个房间内正在运行的任务，从而能在此任务运行中进行更新或者停止此任务。
    TaskId: string;

    //ASR、TTS 和 LLM 相关高级配置。
    Config: Partial<{
      // 流式语音识别相关配置。
      ASRConfig: {
        //语音识别服务提供商。支持取值及含义如下： volcano：火山引擎语音识别。
        Provider: string;
        ProviderParams: Partial<{
          //模型类型。使用流式语音识别服务时取值为smallmodel，使用流式语音识别大模型服务时取值为bigmodel。
          Mode: string;
          //已开通流式语音识别服务的 AppId。
          AppId: string;
          //已开通流式语音识别大模型服务 AppId 对应的 Access Token。 使用流式语音识别大模型服务时该参数为必填。
          AccessToken?: string;
          //流式语音识别大模型开通的服务类型：
          // volc.bigasr.sauc.duration：小时版；
          // volc.bigasr.sauc.concurrent：并发版。
          // 默认值为volc.bigasr.sauc.duration。
          // 该参数仅对流式语音识别大模型服务生效。
          ApiResourceId?: string;
          //具体流式语音识别服务对应的 Cluster ID，可在流式语音服务控制台开通对应服务后查询。 默认为通用-中文的 Cluster ID： volcengine_streaming_common。
          Cluster?: string;
          StreamMode?: number;
          //热词直传，最大值为 200 tokens。
          // 你可以通过传入热词，提高输入词语的识别效果。
          // 该参数仅对流式语音识别大模型服务生效。
          context?: number;
        }>;
        VADConfig?: Partial<{
          //判停时间。停顿时间若高于该值设定时间，则认为一句话结束。
          // 单位为 ms，默认值为 600。
          SilenceTime?: number;
        }>;
        //音量增益值。降低采集音量，以减少噪音引起的 ASR 错误识别。
        // 默认值为 1.0，推荐值 0.3。
        VolumeGain?: number;
      };

      //语音合成相关配置。
      TTSConfig: Partial<{
        //语音合成服务提供商，支持取值及含义如下：volcano：火山引擎单向流式语音合成。volcano_bidirection：火山引擎双向流式语音合成（推荐）。minimax：MiniMax。
        Provider: string;
        ProviderParams: Partial<{
          //火山语音技术应用配置。
          App: Partial<{
            //已开通需要的语音合成服务的 AppId。
            appid?: string;
            //具体语音合成服务对应的 Cluster ID。大模型音频生成-大模型语音合成服务：volcano_tts；
            cluster?: string;
            //已开通需要的语音合成服务的token。 使用火山引擎双向流式语音合成服务时必填。
            token?: string;
          }>;
          //火山语音技术音频配置。
          audio: Partial<{
            //具体语音合成服务开通音色 ID。
            voice_type?: string;
            //语速。语音合成服务：取值范围为[0.2,3]，默认值为 1.0，大模型声音复刻服务：取值范围为[0.8,2]，默认值为 1.0，
            speed_ratio?: number;
            //音量。
            // 取值范围为 [0.1,3]，默认值为 1.0，通常保留一位小数即可
            volume_ratio?: number;
            //音高。
            // 取值范围为 [0.1,3]，默认值为 1.0，通常保留一位小数即可。
            pitch_ratio?: number;
          }>;
          //调用服务的资源信息ID，可取值及含义如下：语音合成大模型：volc.service_type.10029；声音复刻大模型：volc.megatts.default。
          ResourceId?: string;
        }>;
      }>;

      //大模型相关配置，不同的大模型该参数结构不同：
      LLMConfig: Partial<{
        //大模型名称。
        // 方舟大模型： ArkV3。
        //第三方大模型：CustomLLM
        Mode?: string;

        //推理接入点。使用方舟大模型时必填。
        EndPointId?: string;
        //智能体 ID。使用方舟智能体时为必填。
        BotId?: string;
        //输出文本的最大 token 限制。默认值为 1024。
        // 使用方舟大模型和第三方大模型时该参数生效。
        MaxTokens?: number;
        //大模型 System 角色预设指令，可用于控制模型输出。
        SystemMessages?: string[];
        //大模型 User 角色预设 Prompt，可用于增强模型的回复质量，模型回复时会优先参考此处内容。
        UserMessages?: string[];
        //历史问题轮数。默认值为 3。
        HistoryLength?: number;
      }>;

      //打断模式。支持取值及含义如下：0：语音自动打断；1：禁用语音打断。
      InterruptMode?: number;
    }>;

    //智能体基本配置。
    AgentConfig: Partial<{
      //房间内客户端 SDK 用户对应的 UserId。仅支持传入一个 UserId。
      TargetUserId?: string[];
      //智能体启动后的欢迎词。
      WelcomeMessage: string;
      //房间内智能体名称。赋值规则参看参数赋值规范。
      UserId: string;
    }>;

  };
  [ACTIONS.UpdateVoiceChat]: {
    AppId: string;
    BusinessId?: string;
    RoomId?: string;
    TaskId?: string;
    Command: string;
    Message?: string;
    InterruptMode?: Number;
  };
  [ACTIONS.StopVoiceChat]: {
    AppId: string;
    RoomId: string;
    TaskId: string;
  };
}

export interface RequestResponse {
  [ACTIONS.StartVoiceChat]: string;
  [ACTIONS.UpdateVoiceChat]: string;
  [ACTIONS.StopVoiceChat]: string;
}

export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends Array<infer U>
    ? Array<DeepPartial<U>>
    : T[P] extends object
    ? DeepPartial<T[P]>
    : T[P];
};
